/**
 * Browser-calling context: owns the Twilio Device lifecycle and the SINGLE
 * active call, and renders the CallModal so any page can start a call without
 * mounting anything. The Voice SDK is imported dynamically - operators who
 * never dial and whose installation does not answer its phone pay nothing.
 *
 * Phases: idle -> preparing (CallLog + token + SDK in parallel) -> connecting
 * -> ringing -> active -> ended, with error reachable from anywhere. A call
 * coming IN enters at `incoming` and joins the same path at `active` when it is
 * accepted. The modal stays open in ended/error until dismissed; while a call is
 * live, dismissal is blocked (hang up first).
 *
 * The two directions share everything downstream of "there is a call up": the
 * same window, the same note box, the same co-pilot tap. What differs is only
 * how the call arrives - and the device that has to be registered before it can.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import type { Call } from "@twilio/voice-sdk";
import { userCanPlatform, type StaffUser } from "../../lib/auth";
import { normalizeE164 } from "../../lib/phone";
import {
  createCallLog,
  findParkedOffer,
  lookupPhone,
  type ParkedOffer,
} from "../../lib/calls";
import { waitForParkedOffer } from "./parkedOffer";
import { claimOffer, routeAnswerAudio } from "./incoming/claimOffer";
import { applyAudioInput, applyAudioOutputs, type CallAudio } from "./audio/apply";
import { readAudioChoice } from "./audio/choice";
import { armAudioUnlock } from "./audio/context";
import { applyMicGain } from "./audio/gain";
import { readAudioVolume } from "./audio/volume";
import { startCallError } from "./callError";
import { CallContext, type CallContextValue } from "./callContext";
import { CallWindow } from "./CallWindow";
import { useCallBridge } from "./useCallBridge";
import { useVoiceDevice } from "./device/useVoiceDevice";
import { useCallQueue } from "./queue/useCallQueue";
import { IDLE, canStartCall, isLiveCall, type CallPhase, type CallState, type CallTarget } from "./callState";
import { useDialShortcut } from "./useDialShortcut";
import { useUnloadWarning } from "./useUnloadWarning";
import { useCallEvents } from "./useCallEvents";
import { useAiTakeover } from "./takeover";
import { useBusyMode, useCallWaiting, type HeldCall } from "./waiting";

// The vocabulary moved to `callState.ts` when this file outgrew AGENTS.md §4's
// ceiling; it is re-exported here so the fourteen files that import a call's
// types from the provider did not have to change a line.
export type {
  CallDirection,
  CallPhase,
  CallState,
  CallTarget,
  IncomingCall,
} from "./callState";
// Travels with the rest of the vocabulary for the same reason: `CallState.link`
// is typed by it, so anything reading the field would otherwise need a second
// import path to name what it just read.
export type { CallLink } from "./linkQuality";
// The context's contract moved to `callContext.ts` for the same reason; the hook
// is re-exported so the screens that read it kept their import path.
export { useCallContext } from "./callContext";

export function CallProvider({ user, children }: { user: StaffUser; children: ReactNode }) {
  // `use`, not `manage`, since the §3.8 split: this flag is what decides whether
  // the browser registers with Twilio at all, so reading the wrong key here is
  // the difference between a phone that rings and one that never does. A
  // supervisor holding only `read:crm.calls.recordings` correctly gets no
  // device - they read the log, they do not answer it.
  const enabled = userCanPlatform(user, "use:crm.calls");
  // The register is a platform-staff surface with its own permission, and the
  // pad's third tab follows it exactly: an operator who cannot open
  // /ariregister does not get to search it from inside a dialog either.
  const canSearchRegister = userCanPlatform(user, "view:ariregister");
  const [state, setState] = useState<CallState>(IDLE);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dialerPrefill, setDialerPrefill] = useState<string | undefined>(undefined);
  const callRef = useRef<Call | null>(null);
  // Monotonic id so a stale async step of an abandoned call cannot mutate state.
  const sessionRef = useRef(0);
  // The phase as of RIGHT NOW, not as of the last render: a double-click fires
  // both clicks before React re-renders, and the second one must be refused
  // rather than tear down the call the first one just started.
  const phaseRef = useRef<CallPhase>("idle");
  useEffect(() => {
    phaseRef.current = state.phase;
  }, [state.phase]);
  // Spend the first click or keypress anywhere in the CRM on the ringtone's
  // audio context, hours before any call arrives. It has to be armed here
  // rather than by the ring itself, because by the time the phone rings there
  // is no gesture left to spend - see `audio/context.ts`. Session-scoped: this
  // provider wraps the whole CRM layout and is mounted for as long as the tab is.
  useEffect(() => armAudioUnlock(), []);
  /**
   * Is the running call a conference? Seeded from the create response BEFORE
   * `device.connect`, because `attach`'s handlers are built once with empty
   * deps and cannot see state. Inbound sets it false - inbound is still the
   * `<Dial><Client>` fan-out and has no room to hold anybody in.
   */
  const conferenceRef = useRef(false);

  // The device hook needs handlers that are defined below it, and those handlers
  // need its `release`. The cycle is broken with a ref rather than by reordering,
  // because one of the two has to be second whichever way round they go.
  const releaseRef = useRef<() => void>(() => {});
  /**
   * The row lookup of the offer on screen, when it is a PARKED caller's.
   *
   * A ref, not state: `acceptCall` has to be able to wait on the lookup that is
   * already running rather than start a second one, and nothing draws from the
   * promise itself - its answer lands in `incoming` like everything else.
   */
  const parkedRef = useRef<Promise<ParkedOffer | null> | null>(null);
  /**
   * The call on hold while a second one is in front (call waiting). Read by the
   * device release below: its browser leg is still up, so neither the device
   * nor the microphone may be let go while it is there. See `waiting/`.
   */
  const heldRef = useRef<HeldCall | null>(null);
  // Same cycle as `releaseRef`: the waiting hook is needed by `handleIncoming`,
  // which the device hook needs, and the audio routing comes from that hook.
  const callAudioRef = useRef<() => CallAudio | null>(() => null);

  /**
   * Twilio says the call is over and this window had not noticed.
   *
   * The second way a call can end, and the one that had been missing. The first
   * is `call.on('disconnect')` below, which travels over the Voice SDK's
   * signalling websocket - so when that websocket is refused (20101 on a lapsed
   * token, then 31009 on every send) the far end could hang up and this window
   * would go on showing a live call for as long as it was left open.
   *
   * Twilio's own duration is preferred over the local timer for exactly that
   * case: a window that noticed late would otherwise report the minutes it spent
   * not noticing as talk time.
   */
  const finishFromServer = useCallback((durationSec: number | null) => {
    const call = callRef.current;
    callRef.current = null;
    // BEFORE `disconnect()`, and the order is the whole point. `disconnect()`
    // reaches the SDK's own `disconnect` event SYNCHRONOUSLY (`_disconnect` ->
    // `_mediaHandler.close()` -> `PeerConnection.close()` ends on a bare
    // `this.onclose()`, which emits it), so the handler below would otherwise
    // queue its updater first, land "ended" with the local timer, and leave
    // Twilio's count to bail out on its own guard. Queued first, this one wins
    // and the handler's `prev.phase === "ended"` guard does what it says.
    setState((prev) => {
      if (prev.phase === "idle" || prev.phase === "ended" || prev.phase === "error") return prev;
      // A takeover the callee hung up on before this browser was put through:
      // the call is over and it never became this operator's.
      if (prev.takeover && prev.phase !== "active") {
        return { ...prev, phase: "error", error: "takeover_gone", finalDuration: null };
      }
      return {
        ...prev,
        phase: "ended",
        // Not Twilio's count on a takeover: that is the whole call, AI part
        // included, and the operator talked for only the end of it.
        finalDuration:
          (prev.takeover ? null : durationSec) ??
          (prev.answeredAt ? Math.round((Date.now() - prev.answeredAt) / 1000) : null),
      };
    });
    try {
      // Best effort, and worth attempting even when the transport is what
      // failed: on a call that ended normally this is what lets the microphone
      // go, and on one that did not it is a no-op that throws.
      call?.disconnect();
    } catch {
      // Nothing to send it over. The server has already ended the call.
    }
    releaseRef.current();
  }, []);

  /**
   * What the SERVER knows about the running call: the poll that keeps ended,
   * hold, bridged and recording honest, and the three actions that need the
   * REST API rather than the SDK. Kept in its own file - see useCallBridge.ts.
   */
  const { toggleHold, hangupServerSide, startTransfer, stopTransfer, stopRecording } = useCallBridge({
    state,
    patch: setState,
    onServerEnded: finishFromServer,
  });

  /** Wire up a live `Call`, whichever direction produced it. See `useCallEvents.ts`. */
  const attach = useCallEvents({ callRef, conferenceRef, releaseRef, setState });

  /** What a second call does during a call - this person's own choice. */
  const busyMode = useBusyMode(enabled);
  const audioNow = useCallback(() => callAudioRef.current(), []);
  const waiting = useCallWaiting({
    state,
    setState,
    phaseRef,
    sessionRef,
    callRef,
    conferenceRef,
    heldRef,
    attach,
    callAudio: audioNow,
    busyMode,
  });
  const { busy: waitingBusy, offerWhileBusy, api: waitingApi } = waiting;

  // Cmd+W in the middle of a call ends the call, with nobody asked and nothing
  // to go back to. Read from `state.phase` rather than `phaseRef`: the ref is
  // there so a double-click cannot act twice within one render, and this needs
  // the opposite - to re-run whenever the phase changes, which is what a state
  // dependency does and a ref never will.
  // NOT on a call the operator is taking on their mobile: closing this tab does
  // nothing to a conversation happening on a telephone, and a browser prompt
  // saying otherwise would be the app lying about its own reach. The call
  // carries on and the row is written by the webhooks either way.
  // A customer on hold counts too: their leg is in this tab even while the
  // call in front is over, and closing it would drop them. Down here rather
  // than with the other effects because it reads the waiting hook.
  useUnloadWarning((isLiveCall(state.phase) && !state.viaMobile) || waitingApi.held !== null);

  /**
   * Twilio is offering a call.
   *
   * Nothing is accepted here. The card goes up, the phone rings, and the
   * operator decides - which is also why the row id travels in the TwiML rather
   * than being looked up: the card can name the company before the first
   * request comes back.
   */
  const handleIncoming = useCallback((call: Call) => {
    // Busy: a call is up, on hold, or already knocking. The person's own
    // choice decides between a knock and turning it down at once. The device
    // lets these offers through (`allowIncomingWhileBusy`) precisely so this
    // line runs: left to the SDK, they were dropped without a word and the
    // caller's leg rang out as `no-answer`.
    if (waitingBusy()) {
      offerWhileBusy(call);
      return;
    }

    const session = ++sessionRef.current;
    const live = () => sessionRef.current === session;
    const update = (patch: Partial<CallState>) => {
      if (live()) setState((prev) => ({ ...prev, ...patch }));
    };

    const callLogId = call.customParameters?.get("callLogId") ?? "";
    const from = (call.parameters?.From ?? "").trim();
    // The caller's own answers, when the menu asked them. Read here rather than
    // from the row because the row is not fetched until the call is accepted -
    // and the whole point of these two is that they are on screen BEFORE that.
    const intentDepartment = call.customParameters?.get("intentDepartment") ?? "";
    const locale = call.customParameters?.get("noticeLocale") ?? "";
    // No row id at all is a PARKED caller: the operator is rung into a room by
    // the REST API, whose offer carries no `<Parameter>`. The one thing this
    // browser holds is its own leg sid, and the row is found by that below.
    const legSid = callLogId ? "" : (call.parameters?.CallSid ?? "");

    // No hold on an inbound call, on either bridge yet. A parked caller IS in a
    // room, but the hold route still reads the outbound role layout, so the
    // button stays away until it has been taught the difference.
    conferenceRef.current = false;
    phaseRef.current = "incoming";
    setDialerOpen(false);
    setState({
      ...IDLE,
      phase: "incoming",
      direction: "inbound",
      incoming: { callLogId, from, matches: [], intentDepartment, locale, legSid },
      dialedNumber: from || null,
      callLogId: callLogId || null,
    });

    attach(call, live, update);
    // The caller hung up before anybody reached the button, or another operator
    // took it: Twilio cancels the offer and the card has to go with it.
    call.on("cancel", () => {
      if (!live()) return;
      callRef.current = null;
      phaseRef.current = "idle";
      setState(IDLE);
    });

    // Numbered, so a slower answer for a number the card has since replaced
    // cannot land on top of the right one.
    let lookupSeq = 0;
    const lookupMatches = (number: string) => {
      const seq = ++lookupSeq;
      void lookupPhone(number)
        .then((res) => {
          if (!live() || seq !== lookupSeq) return;
          setState((prev) =>
            prev.incoming ? { ...prev, incoming: { ...prev.incoming, matches: res.matches } } : prev,
          );
        })
        .catch(() => {
          // A number and no name is a worse card, not a broken one.
        });
    };
    if (from) lookupMatches(from);

    // The card is already up with what the offer brought. The row follows, and
    // with it the department, the language and the real number: the caller id
    // the server asked Twilio to show is not guaranteed to arrive, and the
    // row's own copy is the one to trust.
    parkedRef.current = legSid
      ? waitForParkedOffer(legSid, live, findParkedOffer).then((found) => {
          if (!found || !live()) return found;
          setState((prev) =>
            prev.incoming
              ? {
                  ...prev,
                  callLogId: found.callLogId,
                  dialedNumber: found.from || prev.dialedNumber,
                  incoming: {
                    ...prev.incoming,
                    callLogId: found.callLogId,
                    from: found.from || prev.incoming.from,
                    intentDepartment: found.intentDepartment,
                    locale: found.noticeLocale,
                  },
                }
              : prev,
          );
          if (found.from && found.from !== from) lookupMatches(found.from);
          return found;
        })
      : null;
  }, [attach, waitingBusy, offerWhileBusy]);

  const onDeviceError = useCallback((code: number | undefined) => {
    // A device-level failure while a call is up belongs to the call's own
    // handler, which has the context to describe it.
    if (callRef.current) return;
    if (phaseRef.current === "idle") return;
    setState((prev) => {
      // NOTHING TO REPORT ON A CALL THAT IS ALREADY OVER, and the guard has to
      // read `prev` rather than `phaseRef`, which is written from an effect and
      // is a render behind. A refused websocket does not emit once: the SDK
      // retries the handshake and re-emits 20101 and 31009 for as long as the
      // token stays dead - which is precisely the situation the server-side
      // ending exists for. `finishFromServer` lets go of the call before it
      // patches the phase, so without this the first error to arrive in that
      // gap would repaint the ended screen red and take the duration, the
      // redial button and the after-call note box with it.
      if (prev.phase === "ended" || prev.phase === "error") return prev;
      return { ...prev, phase: "error", error: code ? `twilio_${code}` : "device_error" };
    });
  }, []);

  const { acquire, release: releaseDevice, audio: callAudio, reach } = useVoiceDevice({
    enabled,
    onIncoming: handleIncoming,
    onError: onDeviceError,
    // Anything but idle counts as busy, including a call that is still ringing:
    // the registration must not be handed to another window while THIS one is
    // holding the `Device` that a live conversation is running on. A call on
    // hold is such a conversation too.
    busy: () => phaseRef.current !== "idle" || heldRef.current !== null,
  });

  /**
   * Let the device go after a call - unless a call is on hold. Its leg is
   * still up on this device and on this microphone, and the call in front
   * ending is not the end of it.
   */
  const release = useCallback(() => {
    if (!heldRef.current) releaseDevice();
  }, [releaseDevice]);

  useEffect(() => {
    releaseRef.current = release;
    callAudioRef.current = callAudio;
  }, [release, callAudio]);

  const startCall = useCallback(
    (target: CallTarget) => {
      if (!enabled || !canStartCall(phaseRef.current)) return;
      phaseRef.current = "preparing";
      // The pad has done its job the moment a call starts; leaving it mounted
      // behind the live view would put a second dialogue under the first.
      setDialerOpen(false);
      setState({ ...IDLE, phase: "preparing", target });

      const session = ++sessionRef.current;
      const live = () => sessionRef.current === session;
      const update = (patch: Partial<CallState>) => {
        if (live()) setState((prev) => ({ ...prev, ...patch }));
      };

      void (async () => {
        const dialed = normalizeE164(target.phone);
        if (!dialed) {
          update({ phase: "error", error: "invalid_number" });
          return;
        }
        update({ dialedNumber: dialed });

        // Asked BEFORE the request, because it decides whether this browser
        // needs a Device at all - and acquiring one means registering a
        // websocket and opening the microphone, neither of which belongs to a
        // call the operator is taking on their telephone.
        const askedMobile = target.viaMobile === true;

        try {
          const [log, device] = await Promise.all([
            createCallLog({
              toNumber: dialed,
              fromNumber: target.fromNumber,
              companyName: target.companyName,
              regCode: target.regCode,
              leadDomain: target.leadDomain,
              clientId: target.clientId,
              leadId: target.leadId,
              contactId: target.contactId,
              source: target.source,
              record: target.record,
              transcribe: target.transcribe,
              notice: target.notice,
              noticeLocale: target.noticeLocale,
              viaMobile: target.viaMobile,
            }),
            // No Device on the mobile path. See `askedMobile` above; `null` is
            // what makes the branch below type-honest rather than an assertion.
            askedMobile ? Promise.resolve(null) : acquire(),
          ]);
          if (!live()) return;
          // From the row rather than from the token: the server clamps the two
          // flags (no transcript without a recording, none at all without a
          // key), so what came back is what the call will really do.
          const conference = log.bridgeMode === "conference";
          // The ref BEFORE `device.connect`, or `accept` can fire against a
          // stale false and start the timer on an empty room.
          conferenceRef.current = conference;
          // The SERVER's answer, not the pad's request: it re-checks the bridge
          // mode and the saved number, so a stale pad that asked for the mobile
          // is refused rather than answered with a browser call the window
          // would then be waiting for a `Call` object on.
          const onMobile = log.operatorLeg === "mobile";
          update({ recording: log.recordEnabled, callLogId: log.id, conference, viaMobile: onMobile });

          // THE CALL IS ALREADY PLACED. `POST /calls` rang the operator's own
          // phone before it answered, so there is no `device.connect()` to make
          // and no `Call` to attach - and therefore no `accept`, `disconnect` or
          // `error` event will ever arrive for this call.
          //
          // Nothing is lost by that, because the poll was already the authority
          // on all three things the window draws: whether the call is up, whether
          // the two of them can hear each other, and whether it has ended (see
          // `useCallBridge`, and the production bug of 2026-08-11 that made it
          // so). `ringing` is where the phase stops until the customer joins the
          // room, which is the same promotion the browser path takes.
          if (onMobile) {
            update({ phase: "ringing" });
            return;
          }
          // Unreachable by construction - `device` is only null on the branch
          // above - and kept as a real guard rather than a `!`, because the
          // thing that would break if the two ever disagreed is a live call.
          if (!device) {
            update({ phase: "error", error: "connect_failed" });
            release();
            return;
          }

          // IMMEDIATELY BEFORE `connect`, and read from storage rather than
          // carried down from the picker: the choice may have been made in a
          // window that has since been closed, and the microphone must not be
          // held open between calls (see `audio/apply.ts`). Both are awaited -
          // a call that started a tenth of a second earlier on the wrong
          // headset is the failure this whole panel exists to prevent.
          const chosen = readAudioChoice();
          const routing = device.audio as CallAudio | null;
          await applyAudioOutputs(routing, chosen);
          // BEFORE the input, not after. Fitting the gain node asks the SDK to
          // restart whatever input stream it already has, so doing it second
          // would mean opening the microphone twice for one call.
          await applyMicGain(routing, readAudioVolume().input);
          await applyAudioInput(routing, chosen);
          if (!live()) return;

          update({ phase: "connecting" });
          const call = await device.connect({ params: { To: dialed, callLogId: log.id } });
          if (!live()) {
            call.disconnect();
            return;
          }
          attach(call, live, update);
        } catch (err) {
          if (!live()) return;
          update({ phase: "error", error: startCallError(err) });
          release();
        }
      })();
    },
    [enabled, acquire, attach, release],
  );

  /** The list being worked down, if there is one. Below `startCall` because it
   *  is the thing it dials with - see queue/useCallQueue.ts. */
  const { queue, startQueue, nextInQueue, stopQueue } = useCallQueue(startCall);

  /** The dock's "take over in this browser" (ring R6d). See takeover/useAiTakeover.ts. */
  const { takeOverAiCall, callOffTakeover } = useAiTakeover({
    enabled,
    phaseRef,
    sessionRef,
    conferenceRef,
    callRef,
    setState,
    setDialerOpen,
    acquire,
    release,
    attach,
  });

  const acceptCall = useCallback(() => {
    const call = callRef.current;
    if (!call || phaseRef.current !== "incoming") return;
    const known = state.incoming?.callLogId ?? "";
    const legSid = state.incoming?.legSid ?? "";
    const parked = parkedRef.current;
    phaseRef.current = "connecting";
    setState((prev) => ({ ...prev, phase: "connecting" }));

    // Claimed BEFORE the audio starts - see `incoming/claimOffer.ts`, which
    // this and the call-waiting knock share.
    void (async () => {
      const claim = await claimOffer({ callLogId: known, legSid, parked });
      if (claim.refused) {
        // Cancelled while the claim was out: nothing is left to turn down.
        if (callRef.current !== call) return;
        try {
          call.reject();
        } catch {
          // Already gone.
        }
        callRef.current = null;
        sessionRef.current++;
        phaseRef.current = "idle";
        setState(IDLE);
        return;
      }
      if (claim.recording !== null) setState((prev) => ({ ...prev, recording: claim.recording === true }));
      // The caller hung up, or a colleague's answer cancelled this offer, while
      // the row and the claim were on their way. There is nothing left to
      // answer, and `accept` on it would paint an error over a card that
      // already went away.
      if (callRef.current !== call) return;
      await routeAnswerAudio(callAudio);
      try {
        call.accept();
      } catch {
        setState((prev) => ({ ...prev, phase: "error", error: "connect_failed" }));
      }
    })();
  }, [state.incoming, callAudio]);

  const rejectCall = useCallback(() => {
    const call = callRef.current;
    if (!call || phaseRef.current !== "incoming") return;
    try {
      call.reject();
    } catch {
      // Already gone.
    }
    callRef.current = null;
    sessionRef.current++;
    phaseRef.current = "idle";
    setState(IDLE);
  }, []);

  const hangup = useCallback(() => {
    // A TAKEOVER STILL ON ITS WAY IN IS CALLED OFF, NOT HUNG UP. The call on
    // this row is the AI's conversation with the callee, and the server's hangup
    // ends that call - so pressing red while "connecting you" is showing would
    // hang up on a stranger mid-sentence. After the bridge the call is the
    // operator's, and the red button below ends it like any other.
    if (state.takeover && state.phase !== "active" && state.callLogId) {
      callOffTakeover(state.callLogId);
      return;
    }
    const call = callRef.current;
    // BOTH PATHS, ALWAYS, and this is the fix for a button that used to do
    // nothing at all: `call.disconnect()` is a message over the signalling
    // websocket and is instant when that socket is alive, but it is silently
    // dropped when it is not - which is what a lapsed access token does, while
    // the audio carries on and the call looks perfectly normal. The server does
    // not share that dependency. Whichever gets there first ends the call; the
    // poll closes this window either way.
    // ONE server request, not two. The ghost-leg case - the room is up, the
    // customer's phone is ringing, nobody has joined, and a leg created by the
    // REST API does not hang up when we do - is inside it: `hangupPlan` cancels
    // that leg before `endBridge` takes the room down, which is exactly what
    // `bridge/end` did. Firing both raced two copies of the same work against
    // the same sids, and the loser logged a failure for a teardown that had in
    // fact just succeeded. `hangupServerSide` falls back to it when the server
    // declines to touch Twilio at all.
    hangupServerSide();
    if (call) {
      call.disconnect();
      return;
    }
    // Nothing connected yet (preparing/connecting) - abandon the attempt. The
    // server call above still matters here: `device.connect()` may not have
    // resolved while Twilio has had a leg up for a second already.
    sessionRef.current++;
    release();
    phaseRef.current = "ended";
    setState((prev) => ({ ...prev, phase: "ended", finalDuration: null }));
  }, [release, hangupServerSide, state.takeover, state.phase, state.callLogId, callOffTakeover]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    call.mute(!call.isMuted());
  }, []);

  const sendDigit = useCallback((digit: string) => {
    if (!/^[0-9*#]$/.test(digit)) return;
    callRef.current?.sendDigits(digit);
  }, []);

  const dismiss = useCallback(() => {
    const phase = phaseRef.current;
    if (phase !== "ended" && phase !== "error") return;
    // Closing the after-call screen with a customer on hold goes back to them:
    // there is nothing else this window could show, and a held call with no
    // screen is a customer left on hold music for good.
    if (heldRef.current) {
      waitingApi.takeBack();
      return;
    }
    sessionRef.current++;
    release();
    phaseRef.current = "idle";
    setState(IDLE);
    // Closing the window ends the run, and there is no second way to read
    // that. A list that survived out of sight would be a list nobody can get
    // back to - the end-of-call button says as much before it is pressed.
    stopQueue();
  }, [release, stopQueue, waitingApi]);

  const getCallStreams = useCallback(() => {
    const call = callRef.current;
    if (!call) return null;
    // Both are guarded: the SDK hands the remote stream over a moment after the
    // call is accepted, so an early read is legitimately empty rather than broken.
    const local = (call.getLocalStream?.() as MediaStream | undefined) ?? null;
    const remote = (call.getRemoteStream?.() as MediaStream | undefined) ?? null;
    return { local, remote };
  }, []);

  const openDialer = useCallback(
    (prefill?: string) => {
      if (!enabled) return;
      setDialerPrefill(prefill);
      setDialerOpen(true);
    },
    [enabled],
  );
  const closeDialer = useCallback(() => setDialerOpen(false), []);

  // The keyboard's own way to the pad. Here rather than in `CrmLayout`, where
  // the Cmd+K listener lives, for the reason the entry gives: that component is
  // ABOVE this provider, so it cannot reach `openDialer` at all.
  useDialShortcut(openDialer, enabled, state.phase === "idle");

  const value = useMemo<CallContextValue>(
    () => ({
      state,
      enabled,
      canSearchRegister,
      reach,
      startCall,
      takeOverAiCall,
      queue,
      startQueue,
      nextInQueue,
      stopQueue,
      acceptCall,
      rejectCall,
      hangup,
      toggleMute,
      toggleHold,
      startTransfer,
      stopTransfer,
      stopRecording,
      sendDigit,
      dismiss,
      getCallStreams,
      getCallAudio: callAudio,
      waiting: waitingApi,
      dialerOpen,
      dialerPrefill,
      openDialer,
      closeDialer,
    }),
    [state, enabled, canSearchRegister, reach, startCall, takeOverAiCall, queue, startQueue, nextInQueue, stopQueue, acceptCall, rejectCall, hangup, toggleMute, toggleHold, startTransfer, stopTransfer, stopRecording, sendDigit, dismiss, getCallStreams, callAudio, waitingApi, dialerOpen, dialerPrefill, openDialer, closeDialer],
  );

  return (
    <CallContext.Provider value={value}>
      {children}
      <CallWindow />
    </CallContext.Provider>
  );
}
